import { useEffect } from 'react';

interface VideoPlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  source: string;
  sourceId: string | null;
  sourceUrl: string | null;
  title: string;
  startTime: number;
}

function buildEmbedUrl(
  source: string,
  sourceId: string | null,
  sourceUrl: string | null,
  startTime: number
): string | null {
  const start = Math.max(0, Math.floor(startTime));

  if (source === 'youtube' && sourceId) {
    return `https://www.youtube.com/embed/${sourceId}?start=${start}&autoplay=1&rel=0`;
  }

  if (source === 'vimeo' && sourceId) {
    return `https://player.vimeo.com/video/${sourceId}#t=${start}s`;
  }

  if (sourceUrl) {
    return sourceUrl;
  }

  return null;
}

function formatTimestamp(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function VideoPlayerModal({
  isOpen,
  onClose,
  source,
  sourceId,
  sourceUrl,
  title,
  startTime,
}: VideoPlayerModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const embedUrl = buildEmbedUrl(source, sourceId, sourceUrl, startTime);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/75" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-4xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-gray-900 truncate">{title}</p>
            <p className="text-xs text-gray-500">Starting at {formatTimestamp(startTime)}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-3 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="relative bg-black" style={{ paddingBottom: '56.25%' }}>
          {embedUrl ? (
            <iframe
              key={embedUrl}
              src={embedUrl}
              title={title}
              className="absolute inset-0 h-full w-full"
              frameBorder={0}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white">
              <p className="text-sm">No playable source for this video.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
